/**
 * Region items for TagTogglePicker, derived from the same filterTagData.json
 * that powers the index filter RegionPicker — one source of truth.
 *
 * Kept in its own module (rather than TagTogglePicker.tsx) so pickers that
 * only need plain string lists — e.g. the professionals create/edit
 * Expertise input — don't bundle the region dataset.
 */
import filterTagData from "@/data/filterTagData.json";
/**
 * Super regions become groups whose children are their regions — and a
 * region that has subRegions of its own (European Union / EFTA under
 * Europe, USA / Canada under North America) becomes a NESTED group whose
 * children are those subRegion names, so the picker can reveal them behind
 * a group chip instead of flattening them away. Supers with no children
 * (e.g. the "Global" pseudo-super) become plain single tags.
 */
export function buildRegionTagItems() {
  const supers = filterTagData.regions;
  const items = [];
  for (const sup of supers) {
    const children = (sup.regions || []).filter(r => r.name && r.name !== "All records").map(r => {
      const subs = (r.subRegions || []).map(s => s.name).filter(n => n && n !== "All records");
      return subs.length ? {
        label: r.name,
        children: subs
      } : r.name;
    });
    if (children.length === 0) {
      items.push(sup.name);
    } else {
      items.push({
        label: sup.name,
        children
      });
    }
  }
  return items;
}
/**
 * Collapse a flat list of selected region values for display: when every
 * member of a group is present (EU, EFTA, USA, Canada — and recursively a
 * whole super region like Europe), the group label replaces its members.
 * Partially-selected groups keep their individual values. Unknown values
 * pass through unchanged, and each input value is represented exactly once.
 */
export function collapseRegionValuesForDisplay(values) {
  const selected = new Set(values.map(v => v.trim()).filter(Boolean));
  const consumed = new Set();
  const out = [];
  const leavesOf = node => {
    if (typeof node === "string") return [node];
    return node.children.flatMap(leavesOf);
  };
  const allSelected = node => leavesOf(node).every(leaf => selected.has(leaf));
  const visit = node => {
    if (typeof node === "string") {
      if (selected.has(node) && !consumed.has(node)) {
        consumed.add(node);
        out.push({
          label: node
        });
      }
      return;
    }
    const leaves = leavesOf(node);
    if (leaves.length > 0 && allSelected(node)) {
      leaves.forEach(leaf => consumed.add(leaf));
      out.push({
        label: node.label,
        members: leaves
      });
      return;
    }
    node.children.forEach(visit);
  };
  buildRegionTagItems().forEach(visit);

  // Anything not covered by the region tree (stray/legacy values).
  for (const v of selected) {
    if (!consumed.has(v)) out.push({
      label: v
    });
  }
  return out;
}